import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { personLabel } from '../../components/ApprovalLevels.js';

interface MessageRow {
  readonly messageId: string;
  readonly senderId: string;
  /** The sender, named by the server (G18). */
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly body: string;
  readonly createdAt: string;
}

/**
 * GR L27.05 — Chat Thread (`/chat/{threadId}`). Renders the message history of
 * a single conversation channel in chronological order. Read view over the
 * `collab messages` for the channel in the route.
 */
export function GrChatThreadPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const { threadId } = useParams<{ threadId: string }>();
  const [rows, setRows] = useState<MessageRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: MessageRow[] }>(`/v1/collab/messages?channelId=${encodeURIComponent(threadId ?? '')}`, { token })
      // A thread reads oldest-first regardless of the order the API returns.
      .then((r) => {
        if (!alive) return;
        setRows([...r.data].sort((a, b) => a.createdAt.localeCompare(b.createdAt)));
        setError(null);
      })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, threadId]);

  return (
    <main aria-label="chat-thread">
      <h1>{t('gr27.chatThread.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr27.chatThread.loading')}</p> : (
        <>
          <p aria-label="message-count">{t('gr27.chatThread.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr27.chatThread.empty')}</p> : (
            <ul aria-label="message-list">
              {rows.map((m) => (
                <li key={m.messageId}>
                  <strong>{personLabel(m.actorName, m.actorCode) ?? '—'}</strong>{': '}<span>{m.body}</span>
                  {' '}<time>{at(m.createdAt)}</time>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </main>
  );
}
